/**
 * CV Builder — Assistente de CV (Trabadju.cv)
 * Módulo autónomo com apoio de IA — Cabo Verde.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import apiClient from "@/utils/api";
import { openDocument } from "@/utils/documents";
import { CVPreviewThumb } from "@/components/CVPreviewThumb";
import { CVTemplatePicker } from "@/components/CVTemplatePicker";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles, Wand2, Plus, Trash2, Download, ArrowLeft, ArrowRight,
  CheckCircle2, AlertCircle, Loader2, Save, FileText, User,
  Briefcase, GraduationCap, Tag, Languages, Award, ClipboardCheck,
  Info, Calendar, Camera, Users, X,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const safeFileName = (s) => (s || "").normalize("NFKD").replace(/[\u0300-\u036f]/g, "").replace(/[^A-Za-z0-9_-]+/g, "_");

// ---------------------------------------------------------------
//  Config
// ---------------------------------------------------------------

const SECTION_IDS = [
  "personal", "summary", "experience", "education", "skills", "languages",
  "certifications", "availability", "references", "cover_letter", "review",
];

const SECTION_ICONS = {
  personal: User, summary: FileText, experience: Briefcase, education: GraduationCap,
  skills: Tag, languages: Languages, certifications: Award, availability: Calendar,
  references: Users, cover_letter: FileText, review: ClipboardCheck,
};

const SECTION_I18N_KEY = {
  personal: "personal", summary: "summary", experience: "experience", education: "education",
  skills: "skills", languages: "languages", certifications: "certifications",
  availability: "availability", references: "references", cover_letter: "coverLetter", review: "review",
};

const QUICK_LANGUAGE_KEYS = ["portuguese", "creole", "english", "french"];

const emptyCV = {
  first_time_mode: null,
  personal: {
    full_name: "", headline: "", email: "", phone: "", location: "",
    linkedin: "", social: "",
    photo_url: "", birthdate: "", nationality: "", driving_license: [],
  },
  summary: "",
  experience: [],
  education: [],
  skills: [],
  languages: [],
  certifications: [],
  availability: { other_islands: "", return_from_diaspora: "", contract_type: "", salary_min: null, salary_max: null, currency: "CVE" },
  references: [],
  cover_letter: "",
  cover_letter_job: "",
};

// ---------------------------------------------------------------
//  MAIN PAGE
// ---------------------------------------------------------------

export default function CVBuilder() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [cv, setCV] = useState(null);
  const [section, setSection] = useState("personal");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [useBusy, setUseBusy] = useState(false);
  const saveTimer = useRef(null);
  const pendingSave = useRef(null); // { next } — snapshot waiting for debounce
  const inflightSave = useRef(null); // promise of currently-running PUT

  const SECTIONS = SECTION_IDS.map((id) => ({ id, label: t(`cvBuilder.sections.${SECTION_I18N_KEY[id]}`), icon: SECTION_ICONS[id] }));
  const CV_KIND_LABELS = {
    formal: t("cvBuilder.kindLabels.formal"),
    informal: t("cvBuilder.kindLabels.informal"),
    familiar: t("cvBuilder.kindLabels.familiar"),
    voluntariado: t("cvBuilder.kindLabels.voluntariado"),
    estagio: t("cvBuilder.kindLabels.estagio"),
    escolar: t("cvBuilder.kindLabels.escolar"),
  };
  const QUICK_LANGUAGES = QUICK_LANGUAGE_KEYS.map((k) => ({
    name: t(`cvBuilder.quickLanguages.${k}`),
    level: t(`cvBuilder.quickLanguages.${k === "portuguese" || k === "creole" ? "native" : "intermediate"}`),
  }));

  // Hide the global Navbar only while this page is mounted — self-contained,
  // does not touch Navbar.js or App.js.
  useEffect(() => {
    document.body.classList.add("cv-builder-active");
    const style = document.createElement("style");
    style.id = "cv-builder-hide-navbar";
    style.textContent = "body.cv-builder-active > #root > .App > nav { display: none !important; }";
    document.head.appendChild(style);
    return () => {
      document.body.classList.remove("cv-builder-active");
      style.remove();
    };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get(`/cv/me`);
        setCV({
          ...emptyCV,
          ...data,
          personal: { ...emptyCV.personal, ...(data?.personal || {}) },
          availability: { ...emptyCV.availability, ...(data?.availability || {}) },
        });
      } catch (e) {
        if (e?.response?.status === 401) { navigate("/candidate/login"); return; }
        alert(t("cvBuilder.loadError"));
      } finally { setLoading(false); }
    })();
  }, [navigate, t]);

  const runSave = useCallback(async (next) => {
    setSaving(true);
    setSaveMsg(t("cvBuilder.savingMsg"));
    const promise = (async () => {
      try {
        await apiClient.put(`/cv/me`, next);
        setSaveMsg(t("cvBuilder.savedMsg"));
        setTimeout(() => setSaveMsg(""), 1400);
      } catch (e) {
        setSaveMsg(t("cvBuilder.saveErrorMsg"));
        throw e;
      } finally {
        setSaving(false);
      }
    })();
    inflightSave.current = promise;
    try { await promise; } finally {
      if (inflightSave.current === promise) inflightSave.current = null;
    }
  }, [t]);

  const scheduleSave = useCallback((next) => {
    setSaveMsg(t("cvBuilder.savingMsg"));
    pendingSave.current = next;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      const payload = pendingSave.current;
      pendingSave.current = null;
      saveTimer.current = null;
      runSave(payload).catch(() => { /* saveMsg already set */ });
    }, 900);
  }, [runSave, t]);

  // Force any pending debounced save to run NOW, and wait until it (and
  // anything already in-flight) is finished. Buttons that read from the DB
  // (PDF render, attach, use-in-applications) MUST call this first, otherwise
  // the DB still has the previous state.
  const flushSave = useCallback(async () => {
    // Wait for anything currently in flight to finish before we take snapshots.
    while (inflightSave.current || pendingSave.current) {
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
        saveTimer.current = null;
      }
      if (inflightSave.current) {
        try { await inflightSave.current; } catch (_) { /* handled */ }
      }
      const payload = pendingSave.current;
      pendingSave.current = null;
      if (payload) {
        try { await runSave(payload); } catch (_) { /* handled */ }
      }
    }
  }, [runSave]);


  const update = useCallback((patch) => {
    setCV((prev) => {
      const next = typeof patch === "function" ? patch(prev) : { ...prev, ...patch };
      scheduleSave(next);
      return next;
    });
  }, [scheduleSave]);

  const idx = SECTIONS.findIndex((s) => s.id === section);
  const goPrev = () => idx > 0 && setSection(SECTIONS[idx - 1].id);
  const goNext = () => idx < SECTIONS.length - 1 && setSection(SECTIONS[idx + 1].id);

  const downloadPDF = async () => {
    setDownloading(true);
    try {
      await flushSave();
      const nice = safeFileName(cv?.personal?.full_name || "CV");
      openDocument(`/cv/me/pdf?lang=${i18n.language?.slice(0, 2) || "pt"}`, `${nice}_Trabadju.pdf`);
    } catch (e) {
      // Server may return JSON error inside a Blob when responseType=blob — parse it.
      let msg = t("cvBuilder.pdfErrorDefault");
      try {
        if (e?.response?.data instanceof Blob) {
          const txt = await e.response.data.text();
          const parsed = JSON.parse(txt);
          if (parsed?.detail) msg = parsed.detail;
        } else if (e?.response?.data?.detail) {
          msg = e.response.data.detail;
        }
      } catch (_) {}
      alert(msg);
    }
    finally { setDownloading(false); }
  };

  const useInApplications = async () => {
    setUseBusy(true);
    try {
      await flushSave();
      await apiClient.post(`/cv/me/use-in-applications`, { lang: i18n.language?.slice(0, 2) || "pt" });

      alert(t("cvBuilder.useInAppsSuccess"));
    } catch (e) { alert(t("cvBuilder.useInAppsError")); }
    finally { setUseBusy(false); }
  };

  if (loading || !cv) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // ------- First-time gate: only shows the first time the candidate lands -------
  if (cv.first_time_mode === null || cv.first_time_mode === undefined) {
    return (
      <IntroFlow
        cv={cv}
        t={t}
        lang={i18n.language}
        onCompleteCreate={(firstTime) => update({ first_time_mode: firstTime })}
        onCompleteImport={(imported) => update((prev) => ({
          ...prev,
          first_time_mode: false,   // marks intro done
          personal: { ...prev.personal, ...imported.personal, photo_url: prev.personal?.photo_url || "" },
          summary: imported.summary || prev.summary,
          experience: imported.experience?.length ? imported.experience : prev.experience,
          education: imported.education?.length ? imported.education : prev.education,
          skills: imported.skills?.length ? Array.from(new Set([...(prev.skills || []), ...imported.skills])) : prev.skills,
          languages: imported.languages?.length ? imported.languages : prev.languages,
          certifications: imported.certifications?.length ? imported.certifications : prev.certifications,
        }))}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <Helmet>
        <title>{t("cvBuilder.helmetTitle")}</title>
        <style>{`body.cv-builder-active > #root > .App > nav { display: none !important; }`}</style>
      </Helmet>

      {/* Top bar */}
      <div className="border-b bg-white/85 backdrop-blur sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-y-2 gap-x-2">
          <div className="flex items-center gap-2 min-w-0">
            <Button variant="ghost" size="sm" onClick={() => navigate("/candidate/profile")} data-testid="cv-back-btn" className="px-2">
              <ArrowLeft className="h-4 w-4 sm:mr-1" /> <span className="hidden sm:inline">{t("cvBuilder.topbar.back")}</span>
            </Button>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="h-4 w-4 text-white" />
                </div>
                <h1 className="text-base sm:text-lg font-black text-slate-900 leading-tight truncate">{t("cvBuilder.topbar.title")}</h1>
              </div>
              <p className="text-[11px] text-slate-500 hidden sm:block">{t("cvBuilder.topbar.subtitle")}</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
            <span className="text-xs text-slate-500 mr-1 hidden md:inline min-w-[80px] text-right">
              {saving ? <Loader2 className="inline h-3 w-3 animate-spin" /> : <Save className="inline h-3 w-3 mr-1" />}
              {saveMsg}
            </span>
            <Button variant="outline" size="sm" onClick={downloadPDF} disabled={downloading} data-testid="cv-download-pdf-btn" className="px-2 sm:px-3">
              {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
              <span className="hidden md:inline ml-1">{t("cvBuilder.topbar.download")}</span>
              <span className="ml-1">{t("cvBuilder.topbar.pdf")}</span>
            </Button>
            <Button size="sm" className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-2 sm:px-3" onClick={useInApplications} disabled={useBusy} data-testid="cv-use-in-apps-btn">
              {useBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
              <span className="ml-1 hidden md:inline">{t("cvBuilder.topbar.useInApps")}</span>
              <span className="ml-1 md:hidden">{t("cvBuilder.topbar.useShort")}</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 grid grid-cols-12 gap-5">
        {/* Left rail */}
        <aside className="col-span-12 md:col-span-3 lg:col-span-2">
          <Card className="p-2 sticky top-24 max-md:overflow-x-auto">
            <nav className="flex md:flex-col gap-1 md:gap-0.5" data-testid="cv-section-nav">
              {SECTIONS.map((s, i) => {
                const Icon = s.icon;
                const active = section === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => setSection(s.id)}
                    data-testid={`cv-nav-${s.id}`}
                    className={`flex-shrink-0 flex items-center gap-2 text-left px-3 py-2 rounded-md text-sm transition ${
                      active
                        ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold shadow"
                        : "text-slate-700 hover:bg-slate-100"
                    }`}
                  >
                    <Icon className="h-4 w-4 flex-shrink-0" />
                    <span className="whitespace-nowrap md:whitespace-normal">{i + 1}. {s.label}</span>
                  </button>
                );
              })}
            </nav>
          </Card>
        </aside>

        {/* Center */}
        <main className="col-span-12 md:col-span-9 lg:col-span-7">
          <Card className="p-5 sm:p-7">
            {section === "personal"       && <SectionPersonal cv={cv} update={update} t={t} />}
            {section === "summary"        && <SectionSummary cv={cv} update={update} t={t} lang={i18n.language} />}
            {section === "experience"     && <SectionExperience cv={cv} update={update} t={t} CV_KIND_LABELS={CV_KIND_LABELS} lang={i18n.language} />}
            {section === "education"      && <SectionEducation cv={cv} update={update} t={t} />}
            {section === "skills"         && <SectionSkills cv={cv} update={update} t={t} />}
            {section === "languages"      && <SectionLanguages cv={cv} update={update} t={t} QUICK_LANGUAGES={QUICK_LANGUAGES} />}
            {section === "certifications" && <SectionCertifications cv={cv} update={update} t={t} />}
            {section === "availability"   && <SectionAvailability cv={cv} update={update} t={t} />}
            {section === "references"     && <SectionReferences cv={cv} update={update} t={t} />}
            {section === "cover_letter"   && <SectionCoverLetter cv={cv} update={update} flushSave={flushSave} t={t} lang={i18n.language} />}
            {section === "review"         && <SectionReview cv={cv} t={t} CV_KIND_LABELS={CV_KIND_LABELS} onDownload={downloadPDF} onUse={useInApplications} downloading={downloading} useBusy={useBusy} onTemplate={(template) => update({ template })} />}

            <div className="flex justify-between mt-8 pt-6 border-t">
              <Button variant="outline" onClick={goPrev} disabled={idx === 0} data-testid="cv-prev-btn">
                <ArrowLeft className="h-4 w-4 mr-1" /> {t("cvBuilder.prev")}
              </Button>
              <Button onClick={goNext} disabled={idx === SECTIONS.length - 1} className="bg-blue-600 hover:bg-blue-700" data-testid="cv-next-btn">
                {t("cvBuilder.next")} <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </Card>
        </main>

        {/* Right AI panel */}
        <aside className="col-span-12 lg:col-span-3">
          <AIPanel
            section={section}
            cv={cv}
            t={t}
            lang={i18n.language}
            onApplySummary={(text) => update({ summary: text })}
            onAppendSkills={(list) => update((prev) => ({
              ...prev,
              skills: Array.from(new Set([...(prev.skills || []), ...list])),
            }))}
          />
        </aside>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------
//  INTRO FLOW — chooses between Criar do zero / Importar CV existente
// ---------------------------------------------------------------

function IntroFlow({ cv, t, lang, onCompleteCreate, onCompleteImport }) {
  const [step, setStep] = useState("choose"); // "choose" | "firsttime" | "import" | "review"
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const handleUpload = async (file) => {
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("lang", (lang || "pt").slice(0, 2));
      const { data } = await apiClient.post(`/cv/import`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      setImportResult(data);
      setStep("review");
    } catch (e) {
      alert(e?.response?.data?.detail || t("cvBuilder.intro.import.uploadError"));
    } finally {
      setImporting(false);
    }
  };

  if (step === "choose") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center p-4">
        <Card className="max-w-2xl w-full p-8 sm:p-10 shadow-xl border-0">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="text-xs font-bold text-blue-700 tracking-widest">{t("cvBuilder.intro.choose.badge")}</div>
              <h2 className="text-xl font-black text-slate-900">{t("cvBuilder.intro.choose.title")}</h2>
            </div>
          </div>
          <p className="text-slate-700 mb-6 leading-relaxed">{t("cvBuilder.intro.choose.lead")}</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <button data-testid="intro-create-zero"
              onClick={() => setStep("firsttime")}
              className="text-left p-5 rounded-xl border-2 border-slate-200 hover:border-blue-500 hover:bg-blue-50 transition group">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="h-5 w-5 text-blue-600" />
                <div className="font-bold text-slate-900 group-hover:text-blue-700">{t("cvBuilder.intro.choose.createTitle")}</div>
              </div>
              <div className="text-sm text-slate-500 leading-relaxed">{t("cvBuilder.intro.choose.createBody")}</div>
            </button>
            <button data-testid="intro-import"
              onClick={() => setStep("import")}
              className="text-left p-5 rounded-xl border-2 border-slate-200 hover:border-indigo-500 hover:bg-indigo-50 transition group">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="h-5 w-5 text-indigo-600" />
                <div className="font-bold text-slate-900 group-hover:text-indigo-700">{t("cvBuilder.intro.choose.importTitle")}</div>
              </div>
              <div className="text-sm text-slate-500 leading-relaxed">{t("cvBuilder.intro.choose.importBody")}</div>
            </button>
          </div>
          <p className="text-[11px] text-slate-400 mt-6 leading-relaxed">{t("cvBuilder.intro.choose.footer")}</p>
        </Card>
      </div>
    );
  }

  if (step === "firsttime") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full p-8 sm:p-10 shadow-xl border-0">
          <button onClick={() => setStep("choose")} className="text-sm text-slate-500 hover:text-slate-800 mb-4 flex items-center gap-1">
            <ArrowLeft className="h-4 w-4" /> {t("cvBuilder.intro.firsttime.back")}
          </button>
          <div className="flex items-center gap-3 mb-6">
            <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="text-xs font-bold text-blue-700 tracking-widest">{t("cvBuilder.intro.firsttime.badge")}</div>
              <h2 className="text-xl font-black text-slate-900">{t("cvBuilder.intro.firsttime.title")}</h2>
            </div>
          </div>
          <p className="text-slate-700 mb-6 leading-relaxed" dangerouslySetInnerHTML={{ __html: t("cvBuilder.intro.firsttime.question") }} />
          <div className="space-y-3">
            <button data-testid="firsttime-yes"
              onClick={() => onCompleteCreate(false)}
              className="w-full text-left p-5 rounded-xl border-2 border-slate-200 hover:border-blue-500 hover:bg-blue-50 transition group">
              <div className="font-bold text-slate-900 group-hover:text-blue-700">{t("cvBuilder.intro.firsttime.yesTitle")}</div>
              <div className="text-sm text-slate-500 mt-1">{t("cvBuilder.intro.firsttime.yesBody")}</div>
            </button>
            <button data-testid="firsttime-no"
              onClick={() => onCompleteCreate(true)}
              className="w-full text-left p-5 rounded-xl border-2 border-slate-200 hover:border-indigo-500 hover:bg-indigo-50 transition group">
              <div className="font-bold text-slate-900 group-hover:text-indigo-700">{t("cvBuilder.intro.firsttime.noTitle")}</div>
              <div className="text-sm text-slate-500 mt-1">{t("cvBuilder.intro.firsttime.noBody")}</div>
            </button>
          </div>
        </Card>
      </div>
    );
  }

  if (step === "import") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center p-4">
        <Card className="max-w-xl w-full p-8 sm:p-10 shadow-xl border-0">
          <button onClick={() => setStep("choose")} className="text-sm text-slate-500 hover:text-slate-800 mb-4 flex items-center gap-1">
            <ArrowLeft className="h-4 w-4" /> {t("cvBuilder.intro.import.back")}
          </button>
          <div className="flex items-center gap-3 mb-6">
            <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="text-xs font-bold text-indigo-700 tracking-widest">{t("cvBuilder.intro.import.badge")}</div>
              <h2 className="text-xl font-black text-slate-900">{t("cvBuilder.intro.import.title")}</h2>
            </div>
          </div>
          <p className="text-slate-700 mb-6 leading-relaxed">{t("cvBuilder.intro.import.body")}</p>

          <label className={`block w-full rounded-xl border-2 border-dashed p-8 text-center cursor-pointer transition ${importing ? "border-indigo-300 bg-indigo-50" : "border-blue-300 bg-blue-50/50 hover:bg-blue-50"}`}
            data-testid="import-upload-label">
            {importing ? (
              <>
                <Loader2 className="h-8 w-8 animate-spin text-indigo-600 mx-auto mb-3" />
                <div className="font-bold text-slate-900">{t("cvBuilder.intro.import.uploadingTitle")}</div>
                <div className="text-xs text-slate-500 mt-1">{t("cvBuilder.intro.import.uploadingHint")}</div>
              </>
            ) : (
              <>
                <FileText className="h-10 w-10 text-blue-600 mx-auto mb-3" />
                <div className="font-bold text-slate-900">{t("cvBuilder.intro.import.uploadCta")}</div>
                <div className="text-xs text-slate-500 mt-1">{t("cvBuilder.intro.import.uploadHint")}</div>
              </>
            )}
            <input type="file" className="hidden"
              accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              disabled={importing}
              onChange={(e) => handleUpload(e.target.files?.[0])}
              data-testid="import-file-input" />
          </label>

          <p className="text-[11px] text-slate-400 mt-4 leading-relaxed" dangerouslySetInnerHTML={{ __html: t("cvBuilder.intro.import.footerNote") }} />
        </Card>
      </div>
    );
  }

  // step === "review"
  return (
    <ImportReview
      t={t}
      result={importResult}
      onRetry={() => { setImportResult(null); setStep("import"); }}
      onAccept={(extracted) => onCompleteImport(extracted)}
      onCancel={() => setStep("choose")}
    />
  );
}

// -------------------------------------------------------------------
//  IMPORT REVIEW — shows extracted data with confidence badges
// -------------------------------------------------------------------

function ConfBadge({ level, t }) {
  let color = "bg-red-100 text-red-800 border-red-200";
  let label = t("cvBuilder.importReview.confLow");
  if (level >= 80) { color = "bg-emerald-100 text-emerald-800 border-emerald-200"; label = t("cvBuilder.importReview.confHigh"); }
  else if (level >= 55) { color = "bg-amber-100 text-amber-800 border-amber-200"; label = t("cvBuilder.importReview.confMedium"); }
  return <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${color}`}>{label} · {level}%</span>;
}

function ReviewSection({ title, conf, t, children }) {
  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-black tracking-widest text-blue-700">{title.toUpperCase()}</div>
        {typeof conf === "number" && <ConfBadge level={conf} t={t} />}
      </div>
      <div className="pl-1">{children}</div>
    </div>
  );
}

function ImportReview({ result, t, onRetry, onAccept, onCancel }) {
  const { extracted, confidence, notes } = result || {};
  if (!extracted) return null;

  const p = extracted.personal || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-4 sm:p-8">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 flex items-center justify-center">
            <ClipboardCheck className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="text-xs font-bold text-indigo-700 tracking-widest">{t("cvBuilder.importReview.badge")}</div>
            <h2 className="text-2xl font-black text-slate-900">{t("cvBuilder.importReview.title")}</h2>
          </div>
        </div>

        {notes && (
          <Alert className="mb-4 bg-amber-50 border-amber-200">
            <AlertCircle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-sm text-slate-700">
              <strong>{t("cvBuilder.importReview.notesPrefix")}</strong> {notes}
            </AlertDescription>
          </Alert>
        )}

        <Alert className="mb-6 bg-blue-50 border-blue-200">
          <Info className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-xs text-slate-700 leading-relaxed">
            <span dangerouslySetInnerHTML={{ __html: t("cvBuilder.importReview.infoIntro") }} />
            <span className="mx-1 inline-flex items-center px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200 text-[10px] font-bold uppercase">{t("cvBuilder.importReview.confMedium")}</span>
            {t("cvBuilder.importReview.infoOr")}
            <span className="mx-1 inline-flex items-center px-1.5 py-0.5 rounded bg-red-100 text-red-800 border border-red-200 text-[10px] font-bold uppercase">{t("cvBuilder.importReview.confLow")}</span>
            {t("cvBuilder.importReview.infoSuffix")}
          </AlertDescription>
        </Alert>

        <Card className="p-6 mb-6">
          <ReviewSection title={t("cvBuilder.importReview.secPersonal")} conf={confidence?.personal} t={t}>
            <div className="text-sm grid sm:grid-cols-2 gap-x-4 gap-y-1">
              <div><span className="text-slate-500">{t("cvBuilder.importReview.fieldName")}</span> <strong>{p.full_name || "—"}</strong></div>
              <div><span className="text-slate-500">{t("cvBuilder.importReview.fieldRole")}</span> <strong>{p.headline || "—"}</strong></div>
              <div><span className="text-slate-500">{t("cvBuilder.importReview.fieldEmail")}</span> <strong>{p.email || "—"}</strong></div>
              <div><span className="text-slate-500">{t("cvBuilder.importReview.fieldPhone")}</span> <strong>{p.phone || "—"}</strong></div>
              <div><span className="text-slate-500">{t("cvBuilder.importReview.fieldLocation")}</span> <strong>{p.location || "—"}</strong></div>
              <div><span className="text-slate-500">{t("cvBuilder.importReview.fieldNationality")}</span> <strong>{p.nationality || "—"}</strong></div>
              <div><span className="text-slate-500">{t("cvBuilder.importReview.fieldBirth")}</span> <strong>{p.birthdate || "—"}</strong></div>
              <div><span className="text-slate-500">{t("cvBuilder.importReview.fieldLicense")}</span> <strong>{(p.driving_license || []).join(", ") || "—"}</strong></div>
            </div>
          </ReviewSection>

          <ReviewSection title={t("cvBuilder.importReview.secSummary")} conf={confidence?.summary} t={t}>
            {extracted.summary
              ? <p className="text-sm text-slate-700 leading-relaxed">{extracted.summary}</p>
              : <p className="text-sm text-slate-400 italic">{t("cvBuilder.importReview.summaryEmpty")}</p>}
          </ReviewSection>

          <ReviewSection title={`${t("cvBuilder.importReview.secExperience")}${extracted.experience?.length ? ` (${extracted.experience.length})` : ""}`} conf={confidence?.experience} t={t}>
            {extracted.experience?.length > 0 ? extracted.experience.map((e, i) => (
              <div key={e.id || i} className="text-sm mb-2 pb-2 border-b border-slate-100 last:border-0">
                <div className="font-bold">{e.title} <span className="text-indigo-600 font-semibold">· {e.company}</span></div>
                <div className="text-xs text-slate-500">{e.start_date} — {e.current ? t("cvBuilder.importReview.current") : e.end_date}</div>
                {e.bullets?.length > 0 && (
                  <ul className="list-disc pl-5 mt-1 text-slate-700 text-[13px]">
                    {e.bullets.slice(0, 3).map((b, bi) => <li key={bi}>{b}</li>)}
                  </ul>
                )}
              </div>
            )) : <p className="text-sm text-slate-400 italic">{t("cvBuilder.importReview.experienceEmpty")}</p>}
          </ReviewSection>

          <ReviewSection title={`${t("cvBuilder.importReview.secEducation")}${extracted.education?.length ? ` (${extracted.education.length})` : ""}`} conf={confidence?.education} t={t}>
            {extracted.education?.length > 0 ? extracted.education.map((e, i) => (
              <div key={e.id || i} className="text-sm mb-1">
                <strong>{e.degree}</strong> · <span className="text-indigo-600">{e.school}</span>
                {(e.start_date || e.end_date) && <span className="text-xs text-slate-500 ml-2">{e.start_date} — {e.end_date}</span>}
              </div>
            )) : <p className="text-sm text-slate-400 italic">{t("cvBuilder.importReview.educationEmpty")}</p>}
          </ReviewSection>

          <ReviewSection title={`${t("cvBuilder.importReview.secSkills")}${extracted.skills?.length ? ` (${extracted.skills.length})` : ""}`} conf={confidence?.skills} t={t}>
            {extracted.skills?.length > 0
              ? <div className="flex flex-wrap gap-1.5">
                  {extracted.skills.map((s) => <span key={s} className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">{s}</span>)}
                </div>
              : <p className="text-sm text-slate-400 italic">{t("cvBuilder.importReview.skillsEmpty")}</p>}
          </ReviewSection>

          <ReviewSection title={`${t("cvBuilder.importReview.secLanguages")}${extracted.languages?.length ? ` (${extracted.languages.length})` : ""}`} conf={confidence?.languages} t={t}>
            {extracted.languages?.length > 0
              ? <div className="text-sm text-slate-700">
                  {extracted.languages.map((l, i) => <span key={i} className="mr-3">{l.name} <span className="text-slate-400">({l.level || "?"})</span></span>)}
                </div>
              : <p className="text-sm text-slate-400 italic">{t("cvBuilder.importReview.languagesEmpty")}</p>}
          </ReviewSection>

          <ReviewSection title={`${t("cvBuilder.importReview.secCertifications")}${extracted.certifications?.length ? ` (${extracted.certifications.length})` : ""}`} conf={confidence?.certifications} t={t}>
            {extracted.certifications?.length > 0
              ? extracted.certifications.map((c, i) => (
                  <div key={c.id || i} className="text-sm"><strong>{c.name}</strong> · <span className="text-slate-500">{c.issuer}</span></div>
                ))
              : <p className="text-sm text-slate-400 italic">{t("cvBuilder.importReview.certificationsEmpty")}</p>}
          </ReviewSection>
        </Card>

        <div className="flex flex-col sm:flex-row gap-3">
          <Button variant="outline" onClick={onRetry} className="flex-1 border-slate-300" data-testid="import-retry-btn">
            {t("cvBuilder.importReview.retry")}
          </Button>
          <Button variant="outline" onClick={onCancel} className="flex-1 border-slate-300">
            {t("cvBuilder.importReview.cancel")}
          </Button>
          <Button onClick={() => onAccept(extracted)}
            className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
            data-testid="import-accept-btn">
            <CheckCircle2 className="h-4 w-4 mr-1" /> {t("cvBuilder.importReview.accept")}
          </Button>
        </div>
        <p className="text-[11px] text-slate-400 mt-4 text-center leading-relaxed">
          {t("cvBuilder.importReview.footerNote")}
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------
//  AI PANEL
// ---------------------------------------------------------------

function AIPanel({ section, cv, t, lang, onApplySummary, onAppendSkills }) {
  const [busy, setBusy] = useState("");
  const [tips, setTips] = useState(null);
  const [suggestion, setSuggestion] = useState(null);

  const call = useCallback(async (kind, payload) => {
    setBusy(kind); setSuggestion(null);
    try {
      const { data } = await apiClient.post(`/cv/ai/${kind}`, { ...payload, lang: (lang || "pt").slice(0, 2) });
      return data;
    } catch (e) {
      alert(e?.response?.data?.detail || t("cvBuilder.aiPanel.unavailable"));
      return null;
    } finally { setBusy(""); }
  }, [t, lang]);

  const genSummary = async () => { const d = await call("summary", { cv }); if (d?.summary) setSuggestion({ type:"summary", text:d.summary }); };
  const genSkills = async () => { const d = await call("skills", { cv }); if (d?.skills?.length) setSuggestion({ type:"skills", list:d.skills }); };
  const genReview = async () => { const d = await call("review", { cv }); if (d) setTips(d); };

  return (
    <div className="space-y-4 sticky top-24" data-testid="cv-ai-panel">
      <Card className="p-5 border-0 shadow-lg bg-gradient-to-br from-indigo-600 via-blue-600 to-purple-600 text-white">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="h-5 w-5" />
          <div className="font-bold tracking-wide">{t("cvBuilder.aiPanel.title")}</div>
        </div>
        <p className="text-xs text-blue-50/90 leading-relaxed">
          {t("cvBuilder.aiPanel.intro")}
        </p>
      </Card>

      {section === "summary" && (
        <Card className="p-5">
          <h4 className="font-bold text-slate-900 mb-1">{t("cvBuilder.aiPanel.summaryTitle")}</h4>
          <p className="text-xs text-slate-500 mb-3">{t("cvBuilder.aiPanel.summaryBody")}</p>
          <Button onClick={genSummary} disabled={busy==="summary"} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="ai-gen-summary-btn">
            {busy==="summary" ? <Loader2 className="h-4 w-4 animate-spin mr-2"/> : <Wand2 className="h-4 w-4 mr-2"/>}
            {t("cvBuilder.aiPanel.suggest")}
          </Button>
          {suggestion?.type==="summary" && (
            <div className="mt-3 p-3 bg-blue-50 rounded-md border border-blue-200">
              <p className="text-sm text-slate-800 whitespace-pre-wrap mb-3">{suggestion.text}</p>
              <div className="flex gap-2">
                <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 flex-1"
                        onClick={() => { onApplySummary(suggestion.text); setSuggestion(null); }}
                        data-testid="ai-accept-summary-btn">
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> {t("cvBuilder.aiPanel.accept")}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setSuggestion(null)}>{t("cvBuilder.aiPanel.reject")}</Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {(section === "experience" || section === "skills") && (
        <Card className="p-5">
          <h4 className="font-bold text-slate-900 mb-1">{t("cvBuilder.aiPanel.skillsTitle")}</h4>
          <p className="text-xs text-slate-500 mb-3">{t("cvBuilder.aiPanel.skillsBody")}</p>
          <Button onClick={genSkills} disabled={busy==="skills"} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="ai-suggest-skills-btn">
            {busy==="skills" ? <Loader2 className="h-4 w-4 animate-spin mr-2"/> : <Sparkles className="h-4 w-4 mr-2"/>}
            {t("cvBuilder.aiPanel.suggestSkills")}
          </Button>
          {suggestion?.type==="skills" && (
            <div className="mt-3 p-3 bg-blue-50 rounded-md border border-blue-200">
              <p className="text-xs text-slate-600 mb-2">{t("cvBuilder.aiPanel.clickToAdd")}</p>
              <div className="flex flex-wrap gap-1.5">
                {suggestion.list.map((s, i) => (
                  <button key={i} onClick={() => onAppendSkills([s])}
                    className="text-xs px-2 py-1 rounded-full bg-white border border-blue-300 hover:bg-blue-600 hover:text-white text-blue-700 transition">
                    + {s}
                  </button>
                ))}
              </div>
              <Button size="sm" variant="ghost" className="mt-2 w-full" onClick={() => onAppendSkills(suggestion.list)}>
                {t("cvBuilder.aiPanel.addAll")}
              </Button>
            </div>
          )}
        </Card>
      )}

      {section === "review" && (
        <Card className="p-5">
          <h4 className="font-bold text-slate-900 mb-1">{t("cvBuilder.aiPanel.reviewTitle")}</h4>
          <p className="text-xs text-slate-500 mb-3">{t("cvBuilder.aiPanel.reviewBody")}</p>
          <Button onClick={genReview} disabled={busy==="review"} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="ai-review-btn">
            {busy==="review" ? <Loader2 className="h-4 w-4 animate-spin mr-2"/> : <ClipboardCheck className="h-4 w-4 mr-2"/>}
            {t("cvBuilder.aiPanel.reviewNow")}
          </Button>
          {tips && (
            <div className="mt-3 space-y-3">
              {tips.missing?.length > 0 && (
                <div className="p-3 rounded-md bg-amber-50 border border-amber-200">
                  <p className="text-xs font-bold text-amber-900 mb-1 flex items-center gap-1">
                    <AlertCircle className="h-3.5 w-3.5" /> {t("cvBuilder.aiPanel.missing")}
                  </p>
                  <ul className="text-xs text-amber-900 space-y-0.5">
                    {tips.missing.map((m, i) => <li key={i}>· {m}</li>)}
                  </ul>
                </div>
              )}
              {tips.tips?.length > 0 && (
                <div className="p-3 rounded-md bg-blue-50 border border-blue-200">
                  <p className="text-xs font-bold text-blue-900 mb-1 flex items-center gap-1">
                    <Sparkles className="h-3.5 w-3.5" /> {t("cvBuilder.aiPanel.tips")}
                  </p>
                  <ul className="text-xs text-slate-800 space-y-1.5">
                    {tips.tips.map((tp, i) => <li key={i}>· {tp}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      <Card className="p-4 bg-slate-50 border-dashed">
        <p className="text-[11px] text-slate-500 leading-relaxed flex gap-2">
          <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
          <span>{t("cvBuilder.aiPanel.footer")}</span>
        </p>
      </Card>
    </div>
  );
}

// Reusable ImproveBtn (inline)
function ImproveBtn({ text, context, onApply, size = "sm", label, t, lang }) {
  const [busy, setBusy] = useState(false);
  const btnLabel = label !== undefined ? label : t("cvBuilder.improveBtn.label");
  const run = async () => {
    if (!text?.trim()) { alert(t("cvBuilder.improveBtn.emptyAlert")); return; }
    setBusy(true);
    try {
      const { data } = await apiClient.post(`/cv/ai/improve`, { text, context, lang: (lang || "pt").slice(0, 2) });
      if (data?.improved && window.confirm(t("cvBuilder.improveBtn.confirmPrompt", { improved: data.improved }))) {
        onApply(data.improved);
      }
    } catch (e) { alert(e?.response?.data?.detail || t("cvBuilder.improveBtn.error")); }
    finally { setBusy(false); }
  };
  return (
    <Button type="button" size={size} variant="outline" onClick={run} disabled={busy}
      className="border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="ai-improve-btn">
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Wand2 className="h-3.5 w-3.5 mr-1" />}
      {btnLabel}
    </Button>
  );
}

// ============================================================
//  SECTION COMPONENTS
// ============================================================

function SectionHeader({ n, title, subtitle }) {
  return (
    <div className="mb-5">
      <h2 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">{n}. {title}</h2>
      {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
    </div>
  );
}

// -------- 1. Sobre ti --------
function SectionPersonal({ cv, update, t }) {
  const p = cv.personal || {};
  const set = (k) => (e) => update({ personal: { ...p, [k]: e.target.value } });

  const uploadPhoto = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await apiClient.post(`/cv/me/photo`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data?.photo_url) update({ personal: { ...p, photo_url: data.photo_url } });
    } catch (e) { alert(e?.response?.data?.detail || t("cvBuilder.personal.photoError")); }
  };

  const removePhoto = async () => {
    try {
      await apiClient.delete(`/cv/me/photo`);
      update({ personal: { ...p, photo_url: "" } });
    } catch (e) { /* ignore */ }
  };

  const toggleLicense = (cat) => {
    const list = p.driving_license || [];
    const next = list.includes(cat) ? list.filter((x) => x !== cat) : [...list, cat];
    update({ personal: { ...p, driving_license: next } });
  };
  const LICENSE_CATS = ["A", "B", "C", "D", "TVDE"];

  return (
    <div>
      <SectionHeader n="1" title={t("cvBuilder.personal.title")} subtitle={t("cvBuilder.personal.subtitle")} />

      {/* Photo + core fields */}
      <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-5 mb-4">
        <div>
          <Label className="text-xs font-semibold text-slate-600">{t("cvBuilder.personal.photoLabel")} <span className="text-slate-400 font-normal">{t("cvBuilder.personal.optional")}</span></Label>
          <div className="mt-1">
            {p.photo_url ? (
              <div className="relative w-[120px] h-[150px] rounded-lg overflow-hidden border">
                <img src={p.photo_url.startsWith("data:") ? p.photo_url : `${API}${p.photo_url}`} alt={t("cvBuilder.personal.photoLabel")} className="w-full h-full object-cover" />
                <button onClick={removePhoto} data-testid="cv-photo-remove-btn" className="absolute top-1 right-1 bg-white/90 rounded-full p-1 shadow" title={t("cvBuilder.personal.removePhoto")}>
                  <X className="h-3.5 w-3.5 text-red-600" />
                </button>
              </div>
            ) : (
              <label className="w-[120px] h-[150px] rounded-lg border-2 border-dashed border-blue-300 bg-blue-50/50 flex flex-col items-center justify-center gap-2 cursor-pointer hover:bg-blue-50 transition text-center">
                <Camera className="h-6 w-6 text-blue-600" />
                <span className="text-xs text-blue-700 font-semibold px-2">{t("cvBuilder.personal.uploadPhoto")}</span>
                <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                  onChange={(e) => uploadPhoto(e.target.files?.[0])} data-testid="cv-photo-input" />
              </label>
            )}
          </div>
          <p className="text-[11px] text-slate-400 mt-1 leading-snug">{t("cvBuilder.personal.photoHint")}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={t("cvBuilder.personal.fullName")}><Input value={p.full_name} onChange={set("full_name")} data-testid="cv-personal-name" /></Field>
          <Field label={t("cvBuilder.personal.headline")} hint={t("cvBuilder.personal.headlineHint")}>
            <Input value={p.headline} onChange={set("headline")} data-testid="cv-personal-headline" />
          </Field>
          <Field label={t("cvBuilder.personal.email")}><Input type="email" value={p.email} onChange={set("email")} data-testid="cv-personal-email" /></Field>
          <Field label={t("cvBuilder.personal.phone")}><Input value={p.phone} onChange={set("phone")} data-testid="cv-personal-phone" /></Field>
          <Field label={t("cvBuilder.personal.location")} hint={t("cvBuilder.personal.locationHint")}><Input value={p.location} onChange={set("location")} data-testid="cv-personal-location" /></Field>
          <Field label={t("cvBuilder.personal.nationality")}><Input value={p.nationality} onChange={set("nationality")} placeholder={t("cvBuilder.personal.nationalityPlaceholder")} /></Field>
        </div>
      </div>

      {/* Optional line */}
      <div className="border-t pt-4 mt-2">
        <p className="text-xs font-semibold text-slate-500 mb-3 tracking-wide uppercase">{t("cvBuilder.personal.optionalFields")}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={t("cvBuilder.personal.birthdate")} hint={t("cvBuilder.personal.birthdateHint")}>
            <Input type="date" value={p.birthdate || ""} onChange={set("birthdate")} data-testid="cv-personal-birthdate" />
          </Field>
          <Field label={t("cvBuilder.personal.linkedin")}><Input value={p.linkedin} onChange={set("linkedin")} placeholder="linkedin.com/in/…" /></Field>
          <Field label={t("cvBuilder.personal.social")} hint={t("cvBuilder.personal.socialHint")}>
            <Input value={p.social} onChange={set("social")} placeholder={t("cvBuilder.personal.socialPlaceholder")} />
          </Field>
          <div>
            <Label className="text-xs font-semibold text-slate-600">{t("cvBuilder.personal.drivingLicense")}</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {LICENSE_CATS.map((c) => {
                const on = (p.driving_license || []).includes(c);
                return (
                  <button key={c} onClick={() => toggleLicense(c)} type="button"
                    data-testid={`cv-license-${c}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold border transition ${
                      on ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-300 hover:border-blue-400"
                    }`}>
                    {c}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-slate-400 mt-1">{t("cvBuilder.personal.drivingLicenseHint")}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// -------- 2. Apresenta-te --------
function SectionSummary({ cv, update, t, lang }) {
  return (
    <div>
      <SectionHeader n="2" title={t("cvBuilder.summary.title")} subtitle={t("cvBuilder.summary.subtitle")} />
      <Textarea rows={6} value={cv.summary || ""} onChange={(e) => update({ summary: e.target.value })}
        placeholder={t("cvBuilder.summary.placeholder")}
        data-testid="cv-summary-text" />
      <div className="mt-3 flex items-center justify-between">
        <span className="text-xs text-slate-400">{t("cvBuilder.summary.charsCount", { count: (cv.summary || "").length })}</span>
        <ImproveBtn text={cv.summary || ""} context={t("cvBuilder.summary.improveContext")}
          onApply={(txt) => update({ summary: txt })} t={t} lang={lang} />
      </div>
    </div>
  );
}

// -------- 3. Experiência (com first-time-mode) --------
function SectionExperience({ cv, update, t, CV_KIND_LABELS, lang }) {
  const list = cv.experience || [];
  const firstTime = cv.first_time_mode === true;

  const add = (kind = "formal") => update({
    experience: [...list, {
      id: crypto.randomUUID(), title: "", company: "", location: "",
      start_date: "", end_date: "", current: false, bullets: [""], kind,
    }],
  });
  const remove = (id) => update({ experience: list.filter((x) => x.id !== id) });
  const patch = (id, k, v) => update({ experience: list.map((x) => x.id === id ? { ...x, [k]: v } : x) });
  const patchBullet = (id, i, v) => update({
    experience: list.map((x) => x.id === id ? { ...x, bullets: x.bullets.map((b, bi) => bi === i ? v : b) } : x),
  });
  const addBullet = (id) => update({ experience: list.map((x) => x.id === id ? { ...x, bullets: [...x.bullets, ""] } : x) });
  const removeBullet = (id, i) => update({ experience: list.map((x) => x.id === id ? { ...x, bullets: x.bullets.filter((_, bi) => bi !== i) } : x) });

  return (
    <div>
      <SectionHeader
        n="3"
        title={firstTime ? t("cvBuilder.experience.titleFirstTime") : t("cvBuilder.experience.titleNormal")}
        subtitle={firstTime ? t("cvBuilder.experience.subtitleFirstTime") : t("cvBuilder.experience.subtitleNormal")} />

      {firstTime && (
        <div className="mb-4 p-3 rounded-lg bg-indigo-50 border border-indigo-200 flex gap-2 items-start">
          <Info className="h-4 w-4 text-indigo-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-slate-700 leading-relaxed">
            {t("cvBuilder.experience.firstTimeInfo")}
          </p>
        </div>
      )}

      <div className="space-y-4">
        {list.map((e) => (
          <Card key={e.id} className="p-4 border-blue-100 relative">
            <button className="absolute top-3 right-3 text-slate-400 hover:text-red-600" onClick={() => remove(e.id)}>
              <Trash2 className="h-4 w-4" />
            </button>

            {firstTime && (
              <div className="mb-3">
                <Label className="text-xs font-semibold text-slate-500">{t("cvBuilder.experience.activityType")}</Label>
                <select value={e.kind || "formal"} onChange={(ev) => patch(e.id, "kind", ev.target.value)}
                        className="mt-1 w-full border rounded-md px-2 py-2 text-sm bg-white">
                  {Object.entries(CV_KIND_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label={firstTime ? t("cvBuilder.experience.whatYouDid") : t("cvBuilder.experience.role")}><Input value={e.title} onChange={(ev) => patch(e.id, "title", ev.target.value)} placeholder={firstTime ? t("cvBuilder.experience.placeholderRoleFirstTime") : t("cvBuilder.experience.placeholderRoleNormal")} /></Field>
              <Field label={firstTime ? t("cvBuilder.experience.whereFor") : t("cvBuilder.experience.company")}><Input value={e.company} onChange={(ev) => patch(e.id, "company", ev.target.value)} placeholder={firstTime ? t("cvBuilder.experience.placeholderCompanyFirstTime") : t("cvBuilder.experience.placeholderCompanyNormal")} /></Field>
              <Field label={t("cvBuilder.experience.location")}><Input value={e.location} onChange={(ev) => patch(e.id, "location", ev.target.value)} /></Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label={t("cvBuilder.experience.start")}><Input value={e.start_date} onChange={(ev) => patch(e.id, "start_date", ev.target.value)} placeholder="2023-01" /></Field>
                <Field label={t("cvBuilder.experience.end")}><Input value={e.end_date} onChange={(ev) => patch(e.id, "end_date", ev.target.value)} placeholder="2024-06" disabled={e.current} /></Field>
              </div>
              <label className="col-span-full flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                <input type="checkbox" checked={e.current} onChange={(ev) => patch(e.id, "current", ev.target.checked)} />
                {t("cvBuilder.experience.currentJob")}
              </label>
            </div>

            <div className="mt-3">
              <Label className="text-xs font-semibold text-slate-500">{t("cvBuilder.experience.bulletsLabel")}</Label>
              <div className="space-y-2 mt-1">
                {e.bullets.map((b, i) => (
                  <div key={i} className="flex gap-2 items-start">
                    <span className="text-blue-600 font-bold mt-2">·</span>
                    <Textarea rows={2} className="flex-1" value={b}
                      onChange={(ev) => patchBullet(e.id, i, ev.target.value)}
                      placeholder={t("cvBuilder.experience.bulletPlaceholder")} />
                    <div className="flex flex-col gap-1">
                      <ImproveBtn text={b}
                        context={t("cvBuilder.experience.improveContext", { kind: CV_KIND_LABELS[e.kind || "formal"], title: e.title || "actividade", company: e.company || "local" })}
                        onApply={(txt) => patchBullet(e.id, i, txt)} label="" size="sm" t={t} lang={lang} />
                      <button className="text-slate-300 hover:text-red-600" onClick={() => removeBullet(e.id, i)}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => addBullet(e.id)} className="text-blue-700 border-blue-200">
                  <Plus className="h-3.5 w-3.5 mr-1" /> {t("cvBuilder.experience.addLine")}
                </Button>
              </div>
            </div>
          </Card>
        ))}

        {firstTime ? (
          <div className="grid sm:grid-cols-2 gap-2">
            <Button onClick={() => add("familiar")} variant="outline" className="border-dashed border-indigo-300 text-indigo-700 hover:bg-indigo-50">
              <Plus className="h-4 w-4 mr-1" /> {t("cvBuilder.experience.addFamily")}
            </Button>
            <Button onClick={() => add("voluntariado")} variant="outline" className="border-dashed border-indigo-300 text-indigo-700 hover:bg-indigo-50">
              <Plus className="h-4 w-4 mr-1" /> {t("cvBuilder.experience.addVolunteer")}
            </Button>
            <Button onClick={() => add("informal")} variant="outline" className="border-dashed border-indigo-300 text-indigo-700 hover:bg-indigo-50">
              <Plus className="h-4 w-4 mr-1" /> {t("cvBuilder.experience.addInformal")}
            </Button>
            <Button onClick={() => add("escolar")} variant="outline" className="border-dashed border-indigo-300 text-indigo-700 hover:bg-indigo-50">
              <Plus className="h-4 w-4 mr-1" /> {t("cvBuilder.experience.addSchool")}
            </Button>
          </div>
        ) : (
          <Button onClick={() => add("formal")} variant="outline" className="w-full border-dashed border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="cv-exp-add-btn">
            <Plus className="h-4 w-4 mr-1" /> {t("cvBuilder.experience.addExperience")}
          </Button>
        )}
      </div>
    </div>
  );
}

// -------- 4. Formação --------
function SectionEducation({ cv, update, t }) {
  const list = cv.education || [];
  const add = () => update({ education: [...list, { id: crypto.randomUUID(), degree: "", school: "", field: "", start_date: "", end_date: "", description: "" }] });
  const patch = (id, k, v) => update({ education: list.map((x) => x.id === id ? { ...x, [k]: v } : x) });
  const remove = (id) => update({ education: list.filter((x) => x.id !== id) });
  return (
    <div>
      <SectionHeader n="4" title={t("cvBuilder.education.title")} subtitle={t("cvBuilder.education.subtitle")} />
      <div className="space-y-4">
        {list.map((e) => (
          <Card key={e.id} className="p-4 border-blue-100 relative">
            <button className="absolute top-3 right-3 text-slate-400 hover:text-red-600" onClick={() => remove(e.id)}><Trash2 className="h-4 w-4" /></button>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label={t("cvBuilder.education.degree")}><Input value={e.degree} onChange={(ev) => patch(e.id, "degree", ev.target.value)} placeholder={t("cvBuilder.education.degreePlaceholder")} /></Field>
              <Field label={t("cvBuilder.education.school")}><Input value={e.school} onChange={(ev) => patch(e.id, "school", ev.target.value)} /></Field>
              <Field label={t("cvBuilder.education.field")}><Input value={e.field} onChange={(ev) => patch(e.id, "field", ev.target.value)} placeholder={t("cvBuilder.education.fieldPlaceholder")} /></Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label={t("cvBuilder.education.start")}><Input value={e.start_date} onChange={(ev) => patch(e.id, "start_date", ev.target.value)} placeholder={t("cvBuilder.education.startPlaceholder")} /></Field>
                <Field label={t("cvBuilder.education.end")}><Input value={e.end_date} onChange={(ev) => patch(e.id, "end_date", ev.target.value)} placeholder={t("cvBuilder.education.endPlaceholder")} /></Field>
              </div>
            </div>
            <div className="mt-3">
              <Label className="text-xs font-semibold text-slate-500">{t("cvBuilder.education.description")}</Label>
              <Textarea rows={2} value={e.description} onChange={(ev) => patch(e.id, "description", ev.target.value)} className="mt-1" />
            </div>
          </Card>
        ))}
        <Button onClick={add} variant="outline" className="w-full border-dashed border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="cv-edu-add-btn">
          <Plus className="h-4 w-4 mr-1" /> {t("cvBuilder.education.add")}
        </Button>
      </div>
    </div>
  );
}

// -------- 5. Competências --------
function SectionSkills({ cv, update, t }) {
  const [input, setInput] = useState("");
  const list = cv.skills || [];
  const add = () => {
    const v = input.trim(); if (!v) return;
    if (list.includes(v)) { setInput(""); return; }
    update({ skills: [...list, v] }); setInput("");
  };
  const remove = (s) => update({ skills: list.filter((x) => x !== s) });
  return (
    <div>
      <SectionHeader n="5" title={t("cvBuilder.skills.title")} subtitle={t("cvBuilder.skills.subtitle")} />
      <div className="flex gap-2 mb-4">
        <Input value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key==="Enter" && (e.preventDefault(), add())}
          placeholder={t("cvBuilder.skills.placeholder")} data-testid="cv-skill-input" />
        <Button onClick={add} className="bg-blue-600 hover:bg-blue-700" data-testid="cv-skill-add-btn">
          <Plus className="h-4 w-4 mr-1" /> {t("cvBuilder.skills.add")}
        </Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {list.map((s) => (
          <Badge key={s} className="bg-blue-100 text-blue-800 border border-blue-200 py-1.5 pl-3 pr-1 gap-1">
            {s}
            <button onClick={() => remove(s)} className="ml-1 rounded-full hover:bg-blue-200 p-1"><Trash2 className="h-3 w-3" /></button>
          </Badge>
        ))}
        {list.length === 0 && <p className="text-sm text-slate-400">{t("cvBuilder.skills.empty")}</p>}
      </div>
    </div>
  );
}

// -------- 6. Línguas --------
function SectionLanguages({ cv, update, t, QUICK_LANGUAGES }) {
  const list = cv.languages || [];
  const add = (name = "", level = "") => update({ languages: [...list, { name, level }] });
  const patch = (i, k, v) => update({ languages: list.map((x, idx) => idx === i ? { ...x, [k]: v } : x) });
  const remove = (i) => update({ languages: list.filter((_, idx) => idx !== i) });
  const levels = [
    t("cvBuilder.languages.levels.native"), t("cvBuilder.languages.levels.fluent"),
    t("cvBuilder.languages.levels.advanced"), t("cvBuilder.languages.levels.intermediate"),
    t("cvBuilder.languages.levels.basic"),
  ];
  const has = (n) => list.some((l) => l.name?.toLowerCase() === n.toLowerCase());
  return (
    <div>
      <SectionHeader n="6" title={t("cvBuilder.languages.title")} subtitle={t("cvBuilder.languages.subtitle")} />

      <div className="mb-4">
        <p className="text-xs font-semibold text-slate-500 mb-2">{t("cvBuilder.languages.quickAdd")}</p>
        <div className="flex flex-wrap gap-2">
          {QUICK_LANGUAGES.map((q) => (
            <button key={q.name} onClick={() => !has(q.name) && add(q.name, q.level)}
              disabled={has(q.name)}
              data-testid={`cv-lang-quick-${q.name}`}
              className={`text-sm px-3 py-1.5 rounded-full border font-semibold transition ${
                has(q.name)
                  ? "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed"
                  : "bg-white text-blue-700 border-blue-300 hover:bg-blue-50"
              }`}>
              + {q.name} <span className="text-slate-400 font-normal">· {q.level}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        {list.map((l, i) => (
          <div key={i} className="flex gap-2 items-center">
            <Input placeholder={t("cvBuilder.languages.namePlaceholder")} value={l.name} onChange={(e) => patch(i, "name", e.target.value)} className="flex-1" />
            <select value={l.level} onChange={(e) => patch(i, "level", e.target.value)} className="border rounded-md px-2 py-2 text-sm bg-white">
              <option value="">{t("cvBuilder.languages.levelPlaceholder")}</option>
              {levels.map((lv) => <option key={lv} value={lv}>{lv}</option>)}
            </select>
            <button onClick={() => remove(i)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        <Button onClick={() => add()} variant="outline" className="w-full border-dashed border-blue-300 text-blue-700 hover:bg-blue-50">
          <Plus className="h-4 w-4 mr-1" /> {t("cvBuilder.languages.add")}
        </Button>
      </div>
    </div>
  );
}

// -------- 7. Certificações --------
function SectionCertifications({ cv, update, t }) {
  const list = cv.certifications || [];
  const add = () => update({ certifications: [...list, { id: crypto.randomUUID(), name: "", issuer: "", date: "", url: "" }] });
  const patch = (id, k, v) => update({ certifications: list.map((x) => x.id === id ? { ...x, [k]: v } : x) });
  const remove = (id) => update({ certifications: list.filter((x) => x.id !== id) });
  return (
    <div>
      <SectionHeader n="7" title={t("cvBuilder.certifications.title")} subtitle={t("cvBuilder.certifications.subtitle")} />
      <div className="space-y-3">
        {list.map((c) => (
          <Card key={c.id} className="p-4 border-blue-100 relative">
            <button className="absolute top-3 right-3 text-slate-400 hover:text-red-600" onClick={() => remove(c.id)}><Trash2 className="h-4 w-4" /></button>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label={t("cvBuilder.certifications.name")}><Input value={c.name} onChange={(e) => patch(c.id, "name", e.target.value)} /></Field>
              <Field label={t("cvBuilder.certifications.issuer")}><Input value={c.issuer} onChange={(e) => patch(c.id, "issuer", e.target.value)} /></Field>
              <Field label={t("cvBuilder.certifications.date")}><Input value={c.date} onChange={(e) => patch(c.id, "date", e.target.value)} placeholder={t("cvBuilder.certifications.datePlaceholder")} /></Field>
              <Field label={t("cvBuilder.certifications.url")}><Input value={c.url} onChange={(e) => patch(c.id, "url", e.target.value)} /></Field>
            </div>
          </Card>
        ))}
        <Button onClick={add} variant="outline" className="w-full border-dashed border-blue-300 text-blue-700 hover:bg-blue-50">
          <Plus className="h-4 w-4 mr-1" /> {t("cvBuilder.certifications.add")}
        </Button>
      </div>
    </div>
  );
}

// -------- 8. Disponibilidade (NEW) --------
function OptRow({ label, name, value, options, onChange }) {
  return (
    <div className="mb-4">
      <Label className="text-sm font-semibold text-slate-700">{label}</Label>
      <div className="flex flex-wrap gap-2 mt-2">
        {options.map((o) => {
          const on = value === o.v;
          return (
            <button key={o.v} type="button" onClick={() => onChange(name, on ? "" : o.v)}
              data-testid={`cv-avail-${name}-${o.v}`}
              className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition ${
                on ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-300 hover:border-blue-400"
              }`}>
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SectionAvailability({ cv, update, t }) {
  const a = cv.availability || {};
  const set = (k, v) => update({ availability: { ...a, [k]: v } });

  return (
    <div>
      <SectionHeader n="8" title={t("cvBuilder.availability.title")} subtitle={t("cvBuilder.availability.subtitle")} />

      <OptRow label={t("cvBuilder.availability.otherIslands")} name="other_islands"
        value={a.other_islands} onChange={set}
        options={[{ v: "sim", label: t("cvBuilder.availability.yes") }, { v: "nao", label: t("cvBuilder.availability.no") }, { v: "depende", label: t("cvBuilder.availability.depends") }]} />

      <OptRow label={t("cvBuilder.availability.returnFromDiaspora")} name="return_from_diaspora"
        value={a.return_from_diaspora} onChange={set}
        options={[
          { v: "imediato", label: t("cvBuilder.availability.immediate") },
          { v: "1_3m", label: t("cvBuilder.availability.range1to3") },
          { v: "3_6m", label: t("cvBuilder.availability.range3to6") },
          { v: "6m_plus", label: t("cvBuilder.availability.plus6") },
        ]} />

      <OptRow label={t("cvBuilder.availability.contractType")} name="contract_type"
        value={a.contract_type} onChange={set}
        options={[
          { v: "efectivo", label: t("cvBuilder.availability.permanent") },
          { v: "termo", label: t("cvBuilder.availability.fixedTerm") },
          { v: "temporario", label: t("cvBuilder.availability.temporary") },
          { v: "part_time", label: t("cvBuilder.availability.partTime") },
          { v: "freelancer", label: t("cvBuilder.availability.freelancer") },
        ]} />

      <div className="mt-2 border-t pt-4">
        <Label className="text-sm font-semibold text-slate-700">{t("cvBuilder.availability.salaryExpectation")} <span className="text-slate-400 font-normal text-xs">{t("cvBuilder.availability.optional")}</span></Label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
          <div>
            <Label className="text-[11px] text-slate-500">{t("cvBuilder.availability.from")}</Label>
            <Input type="number" placeholder="ex.: 40000" value={a.salary_min ?? ""}
              onChange={(e) => set("salary_min", e.target.value ? parseInt(e.target.value, 10) : null)}
              data-testid="cv-avail-salary-min" />
          </div>
          <div>
            <Label className="text-[11px] text-slate-500">{t("cvBuilder.availability.to")}</Label>
            <Input type="number" placeholder="ex.: 60000" value={a.salary_max ?? ""}
              onChange={(e) => set("salary_max", e.target.value ? parseInt(e.target.value, 10) : null)}
              data-testid="cv-avail-salary-max" />
          </div>
          <div>
            <Label className="text-[11px] text-slate-500">{t("cvBuilder.availability.currency")}</Label>
            <select value={a.currency || "CVE"} onChange={(e) => set("currency", e.target.value)}
              className="w-full border rounded-md px-2 py-2 text-sm bg-white">
              <option value="CVE">CVE (Escudo)</option>
              <option value="EUR">EUR</option>
              <option value="USD">USD</option>
            </select>
          </div>
        </div>
        <p className="text-[11px] text-slate-400 mt-2">{t("cvBuilder.availability.salaryHint")}</p>
      </div>
    </div>
  );
}

// -------- 9. Referências (NEW) --------
function SectionReferences({ cv, update, t }) {
  const list = cv.references || [];
  const add = () => update({ references: [...list, { id: crypto.randomUUID(), name: "", role: "", contact: "", relationship: "" }] });
  const patch = (id, k, v) => update({ references: list.map((x) => x.id === id ? { ...x, [k]: v } : x) });
  const remove = (id) => update({ references: list.filter((x) => x.id !== id) });

  return (
    <div>
      <SectionHeader n="9" title={t("cvBuilder.references.title")} subtitle={t("cvBuilder.references.subtitle")} />

      <div className="mb-4 p-3 rounded-lg bg-blue-50 border border-blue-200 flex gap-2 items-start">
        <Info className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-slate-700 leading-relaxed">
          {t("cvBuilder.references.info")}
        </p>
      </div>

      <div className="space-y-3">
        {list.map((r) => (
          <Card key={r.id} className="p-4 border-blue-100 relative">
            <button className="absolute top-3 right-3 text-slate-400 hover:text-red-600" onClick={() => remove(r.id)}><Trash2 className="h-4 w-4" /></button>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label={t("cvBuilder.references.name")}><Input value={r.name} onChange={(e) => patch(r.id, "name", e.target.value)} /></Field>
              <Field label={t("cvBuilder.references.roleCompany")}><Input value={r.role} onChange={(e) => patch(r.id, "role", e.target.value)} placeholder={t("cvBuilder.references.roleCompanyPlaceholder")} /></Field>
              <Field label={t("cvBuilder.references.contact")}><Input value={r.contact} onChange={(e) => patch(r.id, "contact", e.target.value)} /></Field>
              <Field label={t("cvBuilder.references.relationship")}>
                <select value={r.relationship} onChange={(e) => patch(r.id, "relationship", e.target.value)} className="w-full border rounded-md px-2 py-2 text-sm bg-white">
                  <option value="">{t("cvBuilder.references.relationshipChoose")}</option>
                  <option value="old_boss">{t("cvBuilder.references.relOldBoss")}</option>
                  <option value="current_boss">{t("cvBuilder.references.relCurrentBoss")}</option>
                  <option value="teacher">{t("cvBuilder.references.relTeacher")}</option>
                  <option value="volunteer_coord">{t("cvBuilder.references.relVolunteerCoord")}</option>
                  <option value="client">{t("cvBuilder.references.relClient")}</option>
                  <option value="colleague">{t("cvBuilder.references.relColleague")}</option>
                </select>
              </Field>
            </div>
          </Card>
        ))}
        <Button onClick={add} variant="outline" className="w-full border-dashed border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="cv-ref-add-btn">
          <Plus className="h-4 w-4 mr-1" /> {t("cvBuilder.references.add")}
        </Button>
      </div>
    </div>
  );
}

// -------- 10. Carta de Motivação (NEW) --------
function SectionCoverLetter({ cv, update, flushSave, t, lang }) {
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [jobDesc, setJobDesc] = useState(cv.cover_letter_job || "");

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await apiClient.post(
        `/cv/ai/cover-letter`,
        { cv, job_description: jobDesc || "", lang: (lang || "pt").slice(0, 2) },
        { timeout: 90000 }
      );
      if (data?.letter) {
        const replace = !cv.cover_letter || window.confirm(t("cvBuilder.coverLetter.confirmReplace"));
        if (replace) {
          update({ cover_letter: data.letter, cover_letter_job: jobDesc });
        }
      }
    } catch (e) {
      alert(e?.response?.data?.detail || t("cvBuilder.coverLetter.generateError"));
    } finally { setGenerating(false); }
  };

  const downloadPDF = async () => {
    if (!cv.cover_letter?.trim()) { alert(t("cvBuilder.coverLetter.emptyAlert")); return; }
    setDownloading(true);
    try {
      if (flushSave) await flushSave();
      const nice = safeFileName(cv?.personal?.full_name || "Carta");
      openDocument(`/cv/me/cover-letter/pdf?lang=${(lang || "pt").slice(0, 2)}`, `${nice}_CartaMotivacao.pdf`);
    } catch (e) {
      let msg = t("cvBuilder.coverLetter.pdfErrorDefault");
      try {
        if (e?.response?.data instanceof Blob) {
          const txt = await e.response.data.text();
          const parsed = JSON.parse(txt);
          if (parsed?.detail) msg = parsed.detail;
        }
      } catch (_) {}
      alert(msg);
    }
    finally { setDownloading(false); }
  };

  const attach = async () => {
    if (!cv.cover_letter?.trim()) { alert(t("cvBuilder.coverLetter.emptyAlert")); return; }
    setAttaching(true);
    try {
      if (flushSave) await flushSave();
      await apiClient.post(`/cv/me/cover-letter/use-in-applications`, { lang: (lang || "pt").slice(0, 2) });
      alert(t("cvBuilder.coverLetter.attachSuccess"));
    } catch (e) { alert(e?.response?.data?.detail || t("cvBuilder.coverLetter.attachError")); }
    finally { setAttaching(false); }
  };

  return (
    <div>
      <SectionHeader n="10" title={t("cvBuilder.coverLetter.title")} subtitle={t("cvBuilder.coverLetter.subtitle")} />

      {/* Optional job description */}
      <div className="mb-5">
        <Label className="text-sm font-semibold text-slate-700">
          {t("cvBuilder.coverLetter.jobLabel")} <span className="text-xs text-slate-400 font-normal">{t("cvBuilder.coverLetter.jobOptional")}</span>
        </Label>
        <Textarea
          rows={4}
          value={jobDesc}
          onChange={(e) => setJobDesc(e.target.value)}
          placeholder={t("cvBuilder.coverLetter.jobPlaceholder")}
          className="mt-1"
          data-testid="cv-cover-job-desc"
        />
        <p className="text-[11px] text-slate-400 mt-1">{t("cvBuilder.coverLetter.jobHint")}</p>
      </div>

      <Button
        onClick={generate}
        disabled={generating}
        className="w-full mb-5 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:opacity-95"
        data-testid="cv-cover-generate-btn"
      >
        {generating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
        {cv.cover_letter ? t("cvBuilder.coverLetter.regenerate") : t("cvBuilder.coverLetter.generate")}
      </Button>

      {/* Editable letter */}
      <div>
        <Label className="text-sm font-semibold text-slate-700">{t("cvBuilder.coverLetter.yourLetter")}</Label>
        <Textarea
          rows={16}
          value={cv.cover_letter || ""}
          onChange={(e) => update({ cover_letter: e.target.value })}
          placeholder={t("cvBuilder.coverLetter.letterPlaceholder")}
          className="mt-1 font-serif text-[15px] leading-relaxed"
          data-testid="cv-cover-text"
        />
        <div className="text-xs text-slate-400 mt-2 text-right">
          {t("cvBuilder.coverLetter.wordsCount", { count: (cv.cover_letter || "").split(/\s+/).filter(Boolean).length })}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mt-5">
        <Button variant="outline" onClick={downloadPDF} disabled={downloading || !cv.cover_letter?.trim()}
          className="flex-1 border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="cv-cover-download-btn">
          {downloading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
          {t("cvBuilder.coverLetter.downloadPdf")}
        </Button>
        <Button onClick={attach} disabled={attaching || !cv.cover_letter?.trim()}
          className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
          data-testid="cv-cover-attach-btn">
          {attaching ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
          {t("cvBuilder.coverLetter.attach")}
        </Button>
      </div>

      <Alert className="mt-4 bg-blue-50 border-blue-200">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-xs text-slate-700" dangerouslySetInnerHTML={{ __html: t("cvBuilder.coverLetter.footer") }} />
      </Alert>
    </div>
  );
}

// -------- 11. Revisão --------
function SectionReview({ cv, t, CV_KIND_LABELS, onDownload, onUse, downloading, useBusy, onTemplate }) {
  const { i18n } = useTranslation();
  const p = cv.personal || {};
  const a = cv.availability || {};
  const OTHER_ISLANDS_MAP = { sim: t("cvBuilder.availability.yes"), nao: t("cvBuilder.availability.no"), depende: t("cvBuilder.availability.depends") };
  const RETURN_MAP = { imediato: t("cvBuilder.availability.immediate"), "1_3m": t("cvBuilder.availability.range1to3"), "3_6m": t("cvBuilder.availability.range3to6"), "6m_plus": t("cvBuilder.availability.plus6") };
  const CONTRACT_MAP = { efectivo: t("cvBuilder.availability.permanent"), termo: t("cvBuilder.availability.fixedTerm"), temporario: t("cvBuilder.availability.temporary"), part_time: t("cvBuilder.availability.partTime"), freelancer: t("cvBuilder.availability.freelancer") };
  const RELATIONSHIP_MAP = {
    old_boss: t("cvBuilder.references.relOldBoss"), current_boss: t("cvBuilder.references.relCurrentBoss"),
    teacher: t("cvBuilder.references.relTeacher"), volunteer_coord: t("cvBuilder.references.relVolunteerCoord"),
    client: t("cvBuilder.references.relClient"), colleague: t("cvBuilder.references.relColleague"),
  };
  return (
    <div>
      <SectionHeader n="11" title={t("cvBuilder.review.title")} subtitle={t("cvBuilder.review.subtitle")} />

      <div className="mb-5">
        <CVTemplatePicker value={cv?.template || "classic"} onChange={onTemplate} lang={(i18n.language || "pt").slice(0, 2)} refreshKey={cv?.updated_at || ""} />
      </div>
      <div className="mb-5">
        <CVPreviewThumb cv={cv} lang={(i18n.language || "pt").slice(0, 2)} onOpen={onDownload} />
      </div>

      <Card className="p-5 bg-slate-50 border">
        <div className="border-b pb-3 mb-4 flex justify-between items-start gap-4">
          <div className="flex gap-4 items-start">
            {p.photo_url && <img src={p.photo_url.startsWith("data:") ? p.photo_url : `${API}${p.photo_url}`} alt="" className="w-16 h-20 rounded object-cover border" />}
            <div>
              <div className="text-2xl font-black text-slate-900">{p.full_name || "—"}</div>
              <div className="text-sm text-indigo-600 font-semibold">{p.headline}</div>
              <div className="text-xs text-slate-500 mt-1">
                {[p.nationality, p.birthdate, (p.driving_license || []).length ? `${t("cvBuilder.personal.drivingLicense")}: ${p.driving_license.join(", ")}` : ""].filter(Boolean).join(" · ")}
              </div>
            </div>
          </div>
          <div className="text-right text-xs text-slate-600 leading-relaxed shrink-0">
            {p.email && <div>{p.email}</div>}
            {p.phone && <div>{p.phone}</div>}
            {p.location && <div>{p.location}</div>}
          </div>
        </div>

        {cv.summary && <Sec title={t("cvBuilder.review.secProfile")}><p className="text-sm text-slate-700 leading-relaxed">{cv.summary}</p></Sec>}

        {cv.experience?.length > 0 && (
          <Sec title={t("cvBuilder.review.secExperience")}>
            {cv.experience.map((e) => (
              <div key={e.id} className="mb-3">
                <div className="flex justify-between text-sm">
                  <div>
                    <span className="font-bold text-slate-900">{e.title}</span>
                    {e.company && <> · <span className="text-indigo-600 font-semibold">{e.company}</span></>}
                    {e.kind && e.kind !== "formal" && (
                      <span className="ml-2 text-[10px] px-2 py-0.5 rounded bg-slate-200 text-slate-700 font-bold uppercase tracking-wider">{CV_KIND_LABELS[e.kind]}</span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500">{e.start_date} — {e.current ? t("cvBuilder.review.current") : e.end_date}</div>
                </div>
                <ul className="list-disc pl-5 mt-1 text-sm text-slate-700 space-y-0.5">
                  {(e.bullets || []).filter((b) => b.trim()).map((b, i) => <li key={i}>{b}</li>)}
                </ul>
              </div>
            ))}
          </Sec>
        )}

        {cv.education?.length > 0 && (
          <Sec title={t("cvBuilder.review.secEducation")}>
            {cv.education.map((e) => (
              <div key={e.id} className="text-sm mb-2">
                <span className="font-bold">{e.degree}</span> · <span className="text-indigo-600">{e.school}</span>
                <span className="text-xs text-slate-500 ml-2">{e.start_date} — {e.end_date}</span>
              </div>
            ))}
          </Sec>
        )}

        {cv.skills?.length > 0 && (
          <Sec title={t("cvBuilder.review.secSkills")}><div className="flex flex-wrap gap-1.5">{cv.skills.map((s) => <span key={s} className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">{s}</span>)}</div></Sec>
        )}

        {cv.languages?.length > 0 && (
          <Sec title={t("cvBuilder.review.secLanguages")}><div className="text-sm text-slate-700">{cv.languages.map((l, i) => <span key={i} className="mr-3">{l.name} <span className="text-slate-400">({l.level})</span></span>)}</div></Sec>
        )}

        {(a.other_islands || a.contract_type || a.return_from_diaspora || a.salary_min || a.salary_max) && (
          <Sec title={t("cvBuilder.review.secAvailability")}>
            <ul className="text-sm text-slate-700 space-y-1">
              {a.other_islands && <li>{t("cvBuilder.review.otherIslandsLabel")} <strong>{OTHER_ISLANDS_MAP[a.other_islands]}</strong></li>}
              {a.return_from_diaspora && <li>{t("cvBuilder.review.returnLabel")} <strong>{RETURN_MAP[a.return_from_diaspora]}</strong></li>}
              {a.contract_type && <li>{t("cvBuilder.review.contractTypeLabel")} <strong>{CONTRACT_MAP[a.contract_type]}</strong></li>}
              {(a.salary_min || a.salary_max) && <li>{t("cvBuilder.review.salaryLabel")} <strong>{a.salary_min || "?"} — {a.salary_max || "?"} {a.currency || "CVE"}</strong></li>}
            </ul>
          </Sec>
        )}

        {cv.references?.filter((r) => r.name?.trim()).length > 0 && (
          <Sec title={t("cvBuilder.review.secReferences")}>
            <div className="grid sm:grid-cols-2 gap-2 text-sm">
              {cv.references.filter((r) => r.name?.trim()).map((r) => (
                <div key={r.id} className="border-l-2 border-blue-500 pl-2">
                  <div className="font-semibold text-slate-900">{r.name}</div>
                  <div className="text-xs text-indigo-600">{r.role}</div>
                  <div className="text-xs text-slate-600">{r.contact}</div>
                  {r.relationship && <div className="text-[10px] text-slate-500 italic">{RELATIONSHIP_MAP[r.relationship] || r.relationship}</div>}
                </div>
              ))}
            </div>
          </Sec>
        )}
      </Card>

      <div className="flex flex-col sm:flex-row gap-3 mt-6">
        <Button onClick={onDownload} disabled={downloading} variant="outline" className="flex-1 border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="review-download-btn">
          {downloading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
          {t("cvBuilder.review.downloadPdf")}
        </Button>
        <Button onClick={onUse} disabled={useBusy} className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700" data-testid="review-use-btn">
          {useBusy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
          {t("cvBuilder.review.useThisCv")}
        </Button>
      </div>

      <Alert className="mt-5 bg-blue-50 border-blue-200">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-xs text-slate-700" dangerouslySetInnerHTML={{ __html: t("cvBuilder.review.footer") }} />
      </Alert>
    </div>
  );
}

// -------------------------------------------------------------------
//  Small primitives
// -------------------------------------------------------------------
function Field({ label, hint, wide, children }) {
  return (
    <div className={wide ? "col-span-full" : ""}>
      <Label className="text-xs font-semibold text-slate-600">{label}</Label>
      <div className="mt-1">{children}</div>
      {hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}
function Sec({ title, children }) {
  return (
    <div className="mb-4">
      <div className="text-[11px] font-black tracking-widest text-blue-600 mb-2">{title.toUpperCase()}</div>
      {children}
    </div>
  );
}
